/* eslint-disable react/no-unescaped-entities */
"use client";
import React, { useEffect, useCallback } from "react";
import { useState } from "react";
import { useRouter } from "next/router";
import type { NextPage } from "next";
import Image from "next/image";
import Layout from "@/layout/layout";
import banner from "../../assets/images/Calculator-Page.jpg";
import pricecalculator from "../../assets/images/price-calculator.jpg";
import styles from "../gold-price-calculator/price-calculator.module.scss";
import { Accordion } from "react-bootstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import httpClient from "@/api/http-client";
import endpoints from "@/api/endpoints";
import { wrapper } from "@/store";
import {
  setIgnoreCommonTitle,
  setMeta,
  setOg,
  setPath,
  setSchemas,
  setTitle,
} from "@/store/slices/helmet";
type MetalItem = {
  name: string;
  display_name: string;
  price: number;
  updated_at: string;
};

type MetalData = {
  GOLD: MetalItem[];
  PLATINUM: MetalItem[];
  SILVER: MetalItem[];
};

type FormValues = {
  metal: string;
  purity: string;
  weight: string;
  amount: string;
  makingCharge: string;
  gst: string;
};

type CalculationResult = {
  basePrice: number;
  makingCharges: number;
  gstAmount: number;
  totalPrice: number;
  weight: number;
};

const validationSchema = Yup.object({
  metal: Yup.string().required("Metal selection is required"),
  purity: Yup.string().required("Purity selection is required"),
  weight: Yup.string()
    .matches(/^\d+(\.\d{1,2})?$/, "Enter a valid weight (e.g., 1 or 1.5)")
    .required("Weight is required"),
  amount: Yup.string()
    .matches(
      /^\d+(\.\d{1,2})?$/,
      "Enter a valid amount (e.g., 1320 or 1320.50)"
    )
    .required("Amount is required"),
  makingCharge: Yup.string()
    .matches(/^\d+(\.\d{1,2})?%$/, "Enter a valid percentage (e.g., 10%)")
    .required("Making charge is required"),
  gst: Yup.string().required("GST selection is required"),
});

const PriceCalculator: NextPage = () => {
  const router = useRouter();
  const [activeTab, setActiveTab] = useState("price");
  const [metalData, setMetalData] = useState<MetalData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CalculationResult | null>(null);
  const [selectedUpdatedAt, setSelectedUpdatedAt] = useState<string>("");
  const initialValues: FormValues = {
    metal: "SILVER",
    purity: "99.99",
    weight: "1",
    amount: "0",
    makingCharge: "10%",
    gst: "Yes",
  };

  useEffect(() => {
    const fetchMetalData = async () => {
      try {
        const response = await httpClient.get(endpoints.calculator.list());
        setMetalData(response.data);
        const defaultPurity = response.data.SILVER.find(
          (item: MetalItem) => item.name === "99.99"
        );
        if (defaultPurity) {
          setSelectedUpdatedAt(defaultPurity.updated_at);
        }
        setLoading(false);
      } catch (err) {
        setError("Failed to fetch metal data. Please try again later.");
        setLoading(false);
        console.error("Error fetching metal data:", err);
      }
    };

    fetchMetalData();
  }, []);

  const getPurityOptions = (metal: keyof MetalData) => {
    if (!metalData) return [];
    return metalData[metal] || [];
  };

  const calculate = (values: FormValues, tab: string): CalculationResult => {
    if (!metalData)
      return {
        basePrice: 0,
        makingCharges: 0,
        gstAmount: 0,
        totalPrice: 0,
        weight: 0,
      };

    const selectedPurity = metalData[values.metal as keyof MetalData]?.find(
      (item) => item.name === values.purity
    );
    if (!selectedPurity)
      return {
        basePrice: 0,
        makingCharges: 0,
        gstAmount: 0,
        totalPrice: 0,
        weight: 0,
      };

    const pricePerGram = selectedPurity.price;
    const makingChargePercent = parseInt(values.makingCharge.replace("%", ""));
    const includeGST = values.gst === "Yes";

    if (tab === "price") {
      const weight = parseFloat(values.weight) || 0;
      const basePrice = weight * pricePerGram;
      const makingCharges = (basePrice * makingChargePercent) / 100;
      let totalPrice = basePrice + makingCharges;
      const gstAmount = includeGST ? (totalPrice * 3) / 100 : 0;
      totalPrice += gstAmount;

      return {
        basePrice,
        makingCharges,
        gstAmount,
        totalPrice,
        weight,
      };
    } else {
      const amount = parseFloat(values.amount) || 0;
      let amountExcludingGST = amount;

      if (includeGST) {
        amountExcludingGST = amount / 1.03;
      }

      let basePrice = amountExcludingGST;
      if (makingChargePercent > 0) {
        basePrice = amountExcludingGST / (1 + makingChargePercent / 100);
      }

      const weight = basePrice / pricePerGram;

      return {
        basePrice,
        makingCharges: amountExcludingGST - basePrice,
        gstAmount: includeGST ? amount - amountExcludingGST : 0,
        totalPrice: amount,
        weight,
      };
    }
  };

  const handleSubmit = (values: FormValues) => {
    const calculationResult = calculate(values, activeTab);
    setResult(calculationResult);
  };

  const updateAmountFromWeight = useCallback(
    (values: FormValues, setFieldValue: any) => {
      if (activeTab !== "price") return;
      if (!metalData) return;

      const selectedPurity = metalData[values.metal as keyof MetalData]?.find(
        (item) => item.name === values.purity
      );

      if (!selectedPurity) return;

      const weight = parseFloat(values.weight) || 0;
      const pricePerGram = selectedPurity.price;
      const amount = (weight * pricePerGram).toFixed(2);
      setFieldValue("amount", amount);
      setSelectedUpdatedAt(selectedPurity.updated_at);
    },
    [activeTab, metalData]
  );

  const updateWeightFromAmount = useCallback(
    (values: FormValues, setFieldValue: any) => {
      if (activeTab !== "gram") return;
      if (!metalData) return;

      const selectedPurity = metalData[values.metal as keyof MetalData]?.find(
        (item) => item.name === values.purity
      );

      if (!selectedPurity) return;

      const amount = parseFloat(values.amount) || 0;
      const pricePerGram = selectedPurity.price;
      const weight = (amount / pricePerGram).toFixed(2);
      setFieldValue("weight", weight);
      setSelectedUpdatedAt(selectedPurity.updated_at);
    },
    [activeTab, metalData]
  );

  const PriceTabForm = ({
    values,
    setFieldValue,
  }: {
    values: FormValues;
    setFieldValue: any;
  }) => {
    useEffect(() => {
      updateAmountFromWeight(values, setFieldValue);
    }, [
      values.metal,
      values.purity,
      values.weight,
      setFieldValue,
      updateAmountFromWeight,
    ]);

    useEffect(() => {
      if (metalData) {
        const goldItems = metalData.SILVER;
        const defaultPurity = goldItems.find((item) => item.name === "99.99");
        if (defaultPurity) {
          const weight = parseFloat(values.weight) || 0;
          const amount = (weight * defaultPurity.price).toFixed(2);
          setFieldValue("amount", amount);
        }
      }
    }, [metalData, setFieldValue, values.weight]);

    return (
      <Form>
        <div className="py-4">
          <div className="row">
            <div className="col-md-6 ">
              <h5 className="fw-bold mb-4 text-dark">Calculator Input</h5>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">Metal</label>
                <div className="col-md-6">
                  <Field
                    as="select"
                    name="metal"
                    className="form-select"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedMetal = e.target.value as keyof MetalData;

                      if (selectedMetal === "GOLD") {
                        router.push("/gold-price-calculator");
                        return;
                      } else if (selectedMetal === "PLATINUM") {
                        router.push("/platinum-price-calculator");
                        return;
                      }

                      setFieldValue("metal", selectedMetal);
                      const purities = getPurityOptions(selectedMetal);
                      if (purities.length > 0) {
                        setFieldValue("purity", purities[0].name);
                        setSelectedUpdatedAt(purities[0].updated_at);
                      }
                    }}
                  >
                    {metalData &&
                      Object.keys(metalData).map((metal) => (
                        <option key={metal} value={metal}>
                          {metal}
                        </option>
                      ))}
                  </Field>
                  <ErrorMessage
                    name="metal"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">Purity</label>
                <div className="col-md-6">
                  {getPurityOptions(values.metal as keyof MetalData).map(
                    (item) => (
                      <div
                        key={item.name}
                        className="form-check form-check-inline"
                      >
                        <Field
                          type="radio"
                          name="purity"
                          value={item.name}
                          className="form-check-input"
                          id={`purity-${item.name}`}
                          onChange={() => {
                            setFieldValue("purity", item.name);
                            setSelectedUpdatedAt(item.updated_at);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`purity-${item.name}`}
                        >
                          {item.display_name} ({item.name})
                        </label>
                      </div>
                    )
                  )}
                  <ErrorMessage
                    name="purity"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6 pt-md-3">
                  Weight of Metal (in gm)
                </label>
                <div className="col-md-6">
                  <div className={styles.input_group}>
                    <div className={styles.text_input}>
                      <span className={styles.pleft}>Gram</span>
                      <Field
                        type="text"
                        name="weight"
                        className="form-control"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue("weight", e.target.value);
                        }}
                      />
                    </div>
                    <span className={styles.arrow_swip}>⇋</span>
                    <div className={styles.text_input}>
                      <span className={styles.pleft}>Amount</span>
                      <Field
                        type="text"
                        name="amount"
                        className="form-control"
                        readOnly
                        style={{
                          backgroundColor: "#e9ecef",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                  </div>
                  <ErrorMessage
                    name="weight"
                    component="div"
                    className="text-danger"
                  />
                  <ErrorMessage
                    name="amount"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">
                  Making Charge
                </label>
                <div className="col-md-6">
                  <div className="input-group">
                    <Field
                      type="text"
                      name="makingCharge"
                      className="form-control"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        let value = e.target.value.replace(/[^0-9.]/g, "");
                        if (value === "") {
                          value = "0";
                        }
                        value = `${value}%`;
                        setFieldValue("makingCharge", value);
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="makingCharge"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">
                  GST <small>(3%)</small>
                </label>
                <div className="col-md-6">
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      name="gst"
                      value="Yes"
                      className="form-check-input"
                    />
                    <label className="form-check-label">Yes</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      name="gst"
                      value="No"
                      className="form-check-input"
                    />
                    <label className="form-check-label">No</label>
                  </div>
                  <ErrorMessage
                    name="gst"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <button type="submit" className={styles.calculate_btn}>
                Calculate now
              </button>
            </div>

            <div className="col-md-6 ">
              <h5 className="fw-bold mb-4 text-dark mt-4 mt-md-0">
                Calculator Output
              </h5>
              <div className={styles.calculator_output}>
                <div className="w-100 text-center">
                  {result ? (
                    <>
                      <p className="mb-1">
                        {values.weight} Gram of {values.metal} Total Price
                      </p>
                      <h3 className="text-dark">
                        ₹ {result.totalPrice.toFixed(2)}
                      </h3>
                    </>
                  ) : (
                    <p className="text-muted">
                      Click Calculate now to see results
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    );
  };

  const GramTabForm = ({
    values,
    setFieldValue,
  }: {
    values: FormValues;
    setFieldValue: any;
  }) => {
    useEffect(() => {
      updateWeightFromAmount(values, setFieldValue);
    }, [
      values.metal,
      values.purity,
      values.amount,
      setFieldValue,
      updateWeightFromAmount,
    ]);

    return (
      <Form>
        <div className="py-4">
          <div className="row">
            <div className="col-md-6 ">
              <h5 className="fw-bold mb-4 text-dark">Calculator Input</h5>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0  col-md-6">Metal</label>
                <div className="col-md-6">
                  <Field
                    as="select"
                    name="metal"
                    className="form-select"
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const selectedMetal = e.target.value as keyof MetalData;

                      if (selectedMetal === "GOLD") {
                        router.push("/gold-price-calculator");
                        return;
                      } else if (selectedMetal === "PLATINUM") {
                        router.push("/platinum-price-calculator");
                        return;
                      }

                      setFieldValue("metal", selectedMetal);
                      const purities = getPurityOptions(selectedMetal);
                      if (purities.length > 0) {
                        setFieldValue("purity", purities[0].name);
                        setSelectedUpdatedAt(purities[0].updated_at);
                      }
                    }}
                  >
                    {metalData &&
                      Object.keys(metalData).map((metal) => (
                        <option key={metal} value={metal}>
                          {metal}
                        </option>
                      ))}
                  </Field>
                  <ErrorMessage
                    name="metal"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">Purity</label>
                <div className="col-md-6">
                  {getPurityOptions(values.metal as keyof MetalData).map(
                    (item) => (
                      <div
                        key={item.name}
                        className="form-check form-check-inline"
                      >
                        <Field
                          type="radio"
                          name="purity"
                          value={item.name}
                          className="form-check-input"
                          id={`purity-${item.name}`}
                          onChange={() => {
                            setFieldValue("purity", item.name);
                            setSelectedUpdatedAt(item.updated_at);
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor={`purity-${item.name}`}
                        >
                          {item.display_name}
                        </label>
                      </div>
                    )
                  )}
                  <ErrorMessage
                    name="purity"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6 pt-md-3">
                  Weight of Metal (in gm)
                </label>
                <div className="col-md-6">
                  <div className={styles.input_group}>
                    <div className={styles.text_input}>
                      <span className={styles.pleft}>Gram</span>
                      <Field
                        type="text"
                        name="weight"
                        className="form-control"
                        readOnly
                        style={{
                          backgroundColor: "#e9ecef",
                          cursor: "not-allowed",
                        }}
                      />
                    </div>
                    <span className={styles.arrow_swip}>⇋</span>
                    <div className={styles.text_input}>
                      <span className={styles.pleft}>Amount</span>
                      <Field
                        type="text"
                        name="amount"
                        className="form-control"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                          setFieldValue("amount", e.target.value);
                        }}
                      />
                    </div>
                  </div>
                  <ErrorMessage
                    name="weight"
                    component="div"
                    className="text-danger"
                  />
                  <ErrorMessage
                    name="amount"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">
                  Making Charge
                </label>
                <div className="col-md-6">
                  <div className="input-group">
                    <Field
                      type="text"
                      name="makingCharge"
                      className="form-control"
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        let value = e.target.value.replace(/[^0-9.]/g, "");
                        if (value === "") {
                          value = "0";
                        }
                        value = `${value}%`;
                        setFieldValue("makingCharge", value);
                      }}
                    />
                  </div>
                  <ErrorMessage
                    name="makingCharge"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <div className="mb-3 row align-items-center">
                <label className="form-label mb-md-0 col-md-6">
                  GST <small>(3%)</small>
                </label>
                <div className="col-md-6">
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      name="gst"
                      value="Yes"
                      className="form-check-input"
                    />
                    <label className="form-check-label">Yes</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <Field
                      type="radio"
                      name="gst"
                      value="No"
                      className="form-check-input"
                    />
                    <label className="form-check-label">No</label>
                  </div>
                  <ErrorMessage
                    name="gst"
                    component="div"
                    className="text-danger"
                  />
                </div>
              </div>

              <button type="submit" className={styles.calculate_btn}>
                Calculate now
              </button>
            </div>

            <div className="col-md-6 ">
              <h5 className="fw-bold mb-4 text-dark mt-4 mt-md-0">
                Calculator Output
              </h5>
              <div className={styles.calculator_output}>
                <div className="w-100 text-center">
                  {result ? (
                    <>
                      <p className="mb-1">
                        ₹ {values.amount} of {values.metal} Total Weight
                      </p>
                      <h3 className="text-dark">
                        {result.weight.toFixed(2)} Gram
                      </h3>
                    </>
                  ) : (
                    <p className="text-muted">
                      Click Calculate now to see results
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Form>
    );
  };

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-5">Loading...</div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div className="text-center py-5 text-danger">{error}</div>
      </Layout>
    );
  }

  if (!metalData) {
    return (
      <Layout>
        <div className="text-center py-5">No data available</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={styles.banner}>
        <Image src={banner} alt="Price Calculator Banner" />
      </div>

      <div className={styles.calculating_heading_main}>
        <div className="container">
          <h1 className={styles.calculating_heading}>
            Silver Price Calculator
          </h1>
        </div>
      </div>
      <div className={styles.calculator_bg_main}>
        <div className="container pb-5">
          <div className={styles.calculator_card}>
            <div className={styles.calculator_blink}>
              <div className={styles.calculator_blink_text}>
                <div className={styles.live}>Prices updated @</div>
                <div className={styles.calculator_blink_text_price}>
                  <span className={styles.blink}>{selectedUpdatedAt}</span>
                </div>
              </div>
            </div>
            <ul className="nav">
              <li className="nav-item">
                <button
                  className={`nav-link ${
                    activeTab === "price" ? "active" : ""
                  }`}
                  onClick={() => setActiveTab("price")}
                >
                  Calculate Price
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "gram" ? "active" : ""}`}
                  onClick={() => setActiveTab("gram")}
                >
                  Calculate Gram
                </button>
              </li>
            </ul>

            <div className="w-100 p-md-4">
              {activeTab === "price" && (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => (
                    <PriceTabForm
                      values={values}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </Formik>
              )}
              {activeTab === "gram" && (
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({ values, setFieldValue }) => (
                    <GramTabForm
                      values={values}
                      setFieldValue={setFieldValue}
                    />
                  )}
                </Formik>
              )}
            </div>
          </div>
        </div>
        <div className={styles.calculator_image}>
          <Image src={pricecalculator} alt="Price Calculator" />
        </div>
      </div>
      <div className={styles.calculator_footer}>
        <div className="container">
          <h2 className={styles.calculator_footer_text_head}>
            Live Silver Price Calculator
          </h2>
          <p className="mb-4">
            When buying or valuing silver, understanding the price per gram is
            essential. Whether you’re planning to buy silver jewellery, invest
            in silver coins, or sell your silver items, knowing the silver cost
            gram can help you make well-informed decisions. Given that silver
            prices fluctuate daily, a calculator designed to estimate silver’s
            value becomes a practical and time-saving tool.
          </p>
          <p className="mb-4">
            A silver price calculator helps you determine the estimated value of
            your silver based on real-time rates, weight, and purity. Instead of
            manually calculating costs or relying on uncertain quotes, this tool
            gives an instant and approximate value of your silver in Indian
            Rupees (INR). Read on to know more.
          </p>
          <div className="mt-3 mb-5">
            <Accordion>
              <Accordion.Item eventKey="0">
                <Accordion.Header>
                  How Does a Silver Price Calculator Work?
                </Accordion.Header>
                <Accordion.Body>
                  <p>
                    A silver rate calculator is a simple yet effective digital
                    tool that uses your inputs, such as the purity and weight of
                    silver, to calculate the current value. It factors in the
                    silver market rate and gives you a quick estimate. Here's
                    how it typically works:
                  </p>
                  <ul style={{ paddingLeft: "2rem", paddingBottom: "10px" }}>
                    <li>
                      <strong>Weight of Silver:</strong> You enter the weight of
                      your silver item in grams.
                    </li>
                    <li>
                      <strong>Purity Level:</strong> Choose the purity level,
                      like 999 (pure silver) or 925 (sterling silver).
                    </li>
                    <li>
                      <strong>Live Silver Rate:</strong> The tool fetches or
                      allows entry of the current market rate of silver per
                      gram.
                    </li>
                    <li>
                      <strong>Calculation:</strong> The calculator multiplies
                      the weight by the current rate and applies the purity
                      factor to provide the estimated value.
                    </li>
                  </ul>
                  <p>
                    This helps buyers, sellers, and investors get a close
                    approximation of how much their silver item is worth on any
                    given day.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="1">
                <Accordion.Header>
                  Different Purities Used in a Silver Cost Calculator
                </Accordion.Header>
                <Accordion.Body>
                  <p>
                    The silver price calculator allows you to choose among
                    various purities. Purity impacts the final value of silver
                    significantly. Here are the common grades:
                  </p>
                  <h5>999 Silver (Fine Silver)</h5>
                  <p>
                    This is the purest form of silver, containing 99.9% silver
                    content. It is typically used for{" "}
                    <strong>
                      <a href="https://sencogoldanddiamonds.com/jewellery/category/coins-and-bars">
                        coins
                      </a>
                    </strong>
                    , bars, and investments. Items made from 999 silver yield a
                    higher estimated value in calculators.
                  </p>
                  <h5>925 Silver (Sterling Silver)</h5>
                  <p>
                    This is the most widely used grade for jewellery and
                    decorative items. It consists of 92.5% silver and 7.5% of
                    other metals (often copper). A 925 purity selection in the
                    calculator gives a slightly lower estimate than pure silver,
                    but is still highly valued.
                  </p>
                  <h5>Other Purity Grades</h5>
                  <p>
                    Purities like 900 or 800 are used in specific traditional or
                    antique items. You may also find silverware or imported
                    goods marked with these grades, and calculators may provide
                    custom options for such cases.
                  </p>
                  <p>
                    By selecting the appropriate purity, you ensure that the
                    calculator reflects a realistic valuation.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="2">
                <Accordion.Header>
                  Benefits of Using a Silver Rate Calculator
                </Accordion.Header>
                <Accordion.Body>
                  <p>
                    Using a silver cost calculator offers various practical
                    advantages for buyers, sellers, and investors:
                  </p>
                  <p>
                    <strong>1. Instant Valuation:</strong> No need to wait or
                    manually calculate. You receive real-time estimates
                    instantly, helping you stay updated with market trends.
                  </p>
                  <p>
                    <strong>2. Accuracy and Transparency:</strong> The
                    calculator gives consistent results using current rates,
                    helping you avoid price disputes when selling or buying
                    silver.
                  </p>
                  <p>
                    <strong>3. Financial Planning:</strong> Whether you're
                    pledging silver for a loan or planning to invest, an
                    estimate helps you budget effectively.
                  </p>
                  <p>
                    <strong>4. Comparison Shopping:</strong> You can compare
                    prices from different sellers or platforms to ensure you're
                    not overpaying or underselling.
                  </p>
                  <p>
                    <strong>5. Easy to Use:</strong> The interface is usually
                    straightforward, requiring minimal details like weight,
                    purity, and rate per gram.
                  </p>
                  <p>
                    Whether you are a casual buyer or a seasoned investor, this
                    tool can improve decision-making significantly.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="3">
                <Accordion.Header>
                  How to Use a Silver Value Calculator Online
                </Accordion.Header>
                <Accordion.Body>
                  <p>
                    Using a silver value calculator online is quick and
                    hassle-free. Many websites offer this feature, including
                    financial service platforms. Here's a step-by-step guide:
                  </p>
                  <ul style={{ paddingLeft: "2rem", paddingBottom: "10px" }}>
                    <li>
                      <strong>Enter the Weight:</strong> Add the weight of your
                      silver item in grams.
                    </li>
                    <li>
                      <strong>Select Purity:</strong> Choose the purity level
                      (e.g., 925 for sterling silver).
                    </li>
                    <li>
                      <strong>Enter or Use Current Silver Rate:</strong> Some
                      calculators autofill the live rate; others may require
                      manual input.
                    </li>
                    <li>
                      <strong>Making Charges (Optional):</strong> For{" "}
                      <strong>
                        <a href="https://sencogoldanddiamonds.com/jewellery/category/gift-silver">
                          silver jewellery
                        </a>
                      </strong>
                      , you may include making charges per gram if relevant.
                    </li>
                    <li>
                      <strong>GST (if applicable):</strong> Usually 3%,
                      calculated on the total amount.
                    </li>
                    <li>
                      <strong>Calculate:</strong> The calculator shows the
                      estimated value instantly.
                    </li>
                  </ul>
                  <p>
                    <strong>Example:</strong> If you have a 50-gram silver item
                    of 925 purity, and the live rate per gram is ₹70 (approx.),
                    the calculator computes:
                  </p>
                  <p>
                    50 × ₹70 × 92.5% = ₹3,237.50 (excluding taxes or charges).
                  </p>
                  <p>
                    This tool is especially useful when comparing offers or
                    evaluating your silver's worth before transactions.
                  </p>
                  <p>
                    <strong>Disclaimer</strong>
                  </p>
                  <p>
                    The results from any silver calculation tool are indicative
                    in nature. Actual prices may vary due to physical
                    inspection, design, additional charges, and market
                    fluctuations. The calculator is intended for informational
                    purposes only. It does not serve as financial or investment
                    advice, nor is it a certified valuation tool.
                  </p>
                  <p>
                    Users must verify final rates and conditions with trusted
                    jewellers or financial institutions. The estimates generated
                    are based on user inputs and market rates available at the
                    time of usage. The provider of the calculator is not
                    responsible for any discrepancies, errors, or outcomes
                    arising from its use.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
              <Accordion.Item eventKey="4">
                <Accordion.Header>FAQs</Accordion.Header>
                <Accordion.Body>
                  <h3>How does a silver by the gram price calculator work?</h3>
                  <p>
                    A silver by the gram calculator multiplies the silver’s
                    weight by the current price per gram and adjusts the result
                    based on the purity level. It gives you an approximate
                    market value for your silver item.
                  </p>
                  <h3>What is the formula used to calculate silver price?</h3>
                  <p>
                    The formula most commonly used in a silver price today
                    calculator is:
                  </p>
                  <p>
                    Estimated Price = Weight (in grams) × Market Rate per gram ×
                    Purity Factor
                  </p>
                  <p>
                    If applicable, you can add making charges and GST
                    separately.
                  </p>
                  <h3>Does silver price differ as per purity?</h3>
                  <p>
                    Yes, purity directly affects the price. 999 silver has the
                    highest value, followed by 925 (sterling) and lower grades.
                    The chandi calculator adjusts the result based on the
                    selected purity.
                  </p>
                  <h3>What is the silver cost per gram?</h3>
                  <p>
                    The silver cost per gram varies daily and depends on global
                    market trends, demand-supply conditions, and currency
                    exchange rates. Most calculators auto-update the latest
                    price or allow manual entry for accuracy.
                  </p>
                  <h3>Do silver price calculators include GST?</h3>
                  <p>
                    Some calculators include GST (usually 3%) in the final
                    total, especially if making charges are also added. Always
                    check whether the GST is added separately or already
                    included in the displayed amount.
                  </p>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ resolvedUrl }) => {
      // Set canonical path
      store.dispatch(setPath(resolvedUrl));

      // Set page title
      const pageTitle =
        "Silver Price Calculator: Get Today's Silver Rate by Gram | Senco Gold";
      store.dispatch(setTitle(pageTitle));

      // Set meta tags
      store.dispatch(
        setMeta({
          title: pageTitle,
          description:
            "Silver value calculator: Calculate silver price per gram for sterling silver and pure chandi with Senco's silver cost calculator. Get current silver prices by the gram today.",
          keywords:
            "silver cost gram,silver rate calculator,silver price calculator,silver cost calculator,silver value calculator, silver calculation, chandi calculator,silver by the gram calculator,silver price today calculator",
        })
      );

      // Set Open Graph tags
      store.dispatch(
        setOg({
          type: "website",
          url: `${process.env.NEXT_PUBLIC_APP_URL}${resolvedUrl}`,
          title: pageTitle,
          description:
            "Silver value calculator: Calculate silver price per gram for sterling silver and pure chandi with Senco's silver cost calculator. Get current silver prices by the gram today.",
          image: `${process.env.NEXT_PUBLIC_APP_URL}/images/calculator-og-image.jpg`,
        })
      );

      // Ignore common title suffix (already included in custom title)
      store.dispatch(setIgnoreCommonTitle(true));

      // Set structured data schema
      store.dispatch(
        setSchemas([
          JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Silver Price Calculator",
            applicationCategory: "FinanceApplication",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "INR",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: "4.8",
              ratingCount: "1250",
            },
            description:
              "Calculate silver price per gram for sterling silver and pure chandi with Senco's silver cost calculator. Get current silver prices by the gram today.",
            operatingSystem: "Web",
            browserRequirements: "Requires JavaScript. Requires HTML5.",
            url: `${process.env.NEXT_PUBLIC_APP_URL}/silver-price-calculator`,
          }),
          JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: `${process.env.NEXT_PUBLIC_APP_URL}/`,
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "Silver Price Calculator",
                item: `${process.env.NEXT_PUBLIC_APP_URL}/silver-price-calculator`,
              },
            ],
          }),
        ])
      );

      return {
        props: {},
      };
    }
);

export default PriceCalculator;
